import { useEffect, useRef, useState } from "react";
import { PALETTE as P } from "../tokens";
import { authApi } from "../api/auth";

// "Invite a Friend" — surfaces the founding member's personal referral
// code (minted when they landed founding_comp). Self-contained: fetches
// GET /auth/my-referral-code on mount and renders NOTHING when the code
// is null (organic users never see an empty block). Founding comp access
// doesn't expire, so no expiry is shown — kept clean and simple.
//
// referral_code is intentionally NOT on the global user context (UserOut),
// so this component owns the fetch.

function buildShareText(code) {
  return (
    `Join me on Haiven — use code ${code} at signup for free access: ` +
    `https://app.haiven.co/login?mode=signup&code=${code}`
  );
}

// Copy that works on iOS Safari + Android Chrome. Prefers the async
// Clipboard API; falls back to a hidden <textarea> + execCommand for
// older iOS Safari where navigator.clipboard is unavailable or blocked
// outside a secure/again-gestured context.
async function copyText(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length); // iOS needs an explicit range
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export default function ReferAFriend({ token }) {
  const [referralCode, setReferralCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  useEffect(() => {
    if (!token) return;
    let active = true;
    authApi
      .myReferralCode(token)
      .then((r) => {
        if (active) setReferralCode(r?.referral_code || null);
      })
      .catch(() => {
        /* leave null — the section simply won't render */
      });
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  // Organic user (no code) → render nothing. Never show an empty invite block.
  if (!referralCode) return null;

  async function handleCopy() {
    const ok = await copyText(buildShareText(referralCode));
    if (!ok) return;
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      className="fu d3"
      style={{
        background: P.surface,
        borderRadius: 20,
        padding: 22,
        boxShadow: "0 1px 16px rgba(28,24,20,0.07)",
        marginBottom: 12,
      }}
    >
      <div className="section-label" style={{ marginBottom: 10 }}>
        Refer a friend
      </div>

      <div
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 20,
          fontStyle: "italic",
          color: P.text,
          lineHeight: 1.2,
          marginBottom: 10,
        }}
      >
        Invite a Friend
      </div>

      <p
        style={{
          fontSize: 12.5,
          color: P.textMid,
          fontWeight: 300,
          lineHeight: 1.6,
          marginBottom: 16,
        }}
      >
        You're part of the founding cohort. Give someone you trust free
        access — they'll sign up at app.haiven.co with your code below.
      </p>

      {/* The code, front and center */}
      <div
        style={{
          background: P.primarySoft,
          border: `1px solid rgba(158,132,86,0.20)`,
          borderRadius: 14,
          padding: "16px 18px",
          textAlign: "center",
          marginBottom: 14,
        }}
      >
        <div
          data-testid="referral-code"
          style={{
            fontFamily:
              "'SF Mono', 'DM Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 26,
            fontWeight: 600,
            letterSpacing: "0.14em",
            color: P.primary,
            wordBreak: "break-all",
          }}
        >
          {referralCode}
        </div>
      </div>

      {/* Copy button + fading confirmation */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          type="button"
          onClick={handleCopy}
          style={{
            flex: 1,
            padding: "13px 16px",
            background: P.primary,
            color: "#F2EDE3",
            border: "none",
            borderRadius: 12,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13.5,
            fontWeight: 500,
            letterSpacing: "0.03em",
            transition: "opacity 0.15s",
          }}
        >
          Copy invite
        </button>
        {copied && (
          <span
            role="status"
            aria-live="polite"
            className="refer-copied"
            style={{
              fontSize: 12.5,
              color: P.primary,
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            Copied!
          </span>
        )}
      </div>
    </div>
  );
}
